<script setup>
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import EnTextField from 'Components/inputs/EnTextFieldV2/EnTextFieldV2.vue';
import EnOverlaySpinner from 'Components/feedback/EnOverlaySpinner/EnOverlaySpinner';
import EnValidationErrorMessage from 'Components/feedback/EnValidationErrorMessage/EnValidationErrorMessage.vue';
import EnIcon from 'Components/icons/EnIcon.vue';
import EnButton from 'Components/base/EnButton/EnButton.vue';
import EnWidgetCard from 'Components/widgets/EnWidgetCard/EnWidgetCard.vue';

import { required } from 'Js/validation/rules';
import { sleep } from 'Js/utils/sleep';
import { useSessionStorage } from 'Components/navigation/utils/useSessionStorage';
import { getUrlParams } from 'Js/utils/urlUtils';
import { EnButtonVariants } from 'Components/types/EnButtonVariants';
import { HelpLinks } from 'Js/constants/helpLinks';
import { useUserSettingsStore } from 'Js/store/useUserSettingsStore';

const { state, updateState } = storeToRefs(useUserSettingsStore());
const route = useRoute();
const userAccountName = ref(null);
const loading = ref(true);
let globalConfig = null;
const isEnableLocalLogin = ref(null);
const productName = ref();
const clientId = ref(null);
const errorList = ref([]);
const externalProviders = ref([{
  AuthenticationScheme: 'aad',
  DisplayName: 'Microsoft'
}]);
const queryReturnUrl = ref(null);
const shortAccountName = ref(null);
const guidAccount = ref(null);
const antiForgery = ref(null);
const form = ref();
const username = ref(null);
const password = ref(null);
const showPassword = ref(false);
const updateRail = inject('updateRail');
const supportSubject = ref(`mailto:hilfe@enloc.de?Subject=${encodeURIComponent(t('common.help.contactButtonSubject'))}`);
const {setIsEnTab, getIsEnTab} = useSessionStorage();
const isEnTab = computed(() => {
  return route.name === 'LoginEnTab' || getIsEnTab();
});
const findOutMoreUrl = 'https://www.enloc.de/zeit-sich-kennenzulernen/';
const rules = ref({
  required: [val => required(val)],
});

function togglePasswordVisibility() {
  showPassword.value = !showPassword.value;
}

function externalUrl(provider) {
  const query = new URLSearchParams({
    provider: provider.AuthenticationScheme,
  });
  if (queryReturnUrl.value) {
    query.append('returnUrl', queryReturnUrl.value);
  }
  return `/External/Challenge?${query}`;
}

const forgetPasswordUrl = computed(() => {
  const query = new URLSearchParams({ReturnUrl: queryReturnUrl.value});
  return `/Account/Profile/ForgotPassword?${query}`;
});
// Will be used later
const registerLink = computed(() => {
  const query = encodeURIComponent(queryReturnUrl.value);
  return `/Account/Profile/Register?returnUrl=${query}`;
});

async function tryLogin() {
  loading.value = true;
  const response = await fetch(`/Account/TryLogin`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({EmailOrUsername: username.value, Password: password.value, ClientId: clientId.value})
  });
  if (response.ok) {
    const config = await response.json();
    errorList.value = config.validationErrors;
    if (config.validationErrors.length === 0) {
      await form.value.submit();
    } else {
      loading.value = false;
    }
  }
}

onBeforeMount(async () => {
  const params = getUrlParams(window.location);

  for (const [key, value] of new URLSearchParams(window.location.search)) {
    params[key.toLowerCase()] = value;
  }
  const returnUrl = params['returnurl'];

  const additionalParams = new URLSearchParams(returnUrl);
  const accountUrl = additionalParams.get('a');
  const guidUrl = additionalParams.get('g');
  guidAccount.value = guidUrl;

  const requestBody = {returnUrl: returnUrl};
  if (accountUrl) {
    requestBody.AccountName = accountUrl;
    requestBody.Guid = guidUrl;
  }
  const response = await fetch(`/api/Configuration/GetConfigurationForUser`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestBody),
  });
  if (response.ok) {
    const config = await response.json();
    antiForgery.value = config.AntiForgeryToken;
    userAccountName.value = config.AccountDisplayName;
    isEnableLocalLogin.value = config.EnableLocalLogin;
    clientId.value = config.ClientId;
    productName.value = config.ProductName;
    queryReturnUrl.value = config.ReturnUrl;
    shortAccountName.value = config.ShortAccountName;

    globalConfig = {
      EngineUrl: config.EngineUrl,
      AuthUrl: config.AuthUrl,
      NotificationCallbacks: [],
      AddNotificationCallback: (item) => {
        this.globalState?.NotificationCallbacks.push(item);
      },
      EnPowerAccountsForUser: config.EnPowerAccountsForUser,
      EnFlowUrls: config.EnFlowUrls,
      IsAuthUrl: config.IsAuthUrl,
      IsDev: config.IsDev,
      IsLocalApp: config.IsLocalApp,
      AiConnectionString: config.AiConnectionString,
      ModulePermissions: config.ModulePermissions,
      RolesText: config.RolesText,
      WidgetSettings: config.WidgetSettings,
    };
  }
  await sleep(500);
  loading.value = false;
  updateState?.(globalConfig);
  useUserSettingsStore()?.updateState?.(globalConfig);
  setIsEnTab(!!isEnTab.value);
});

watch(isEnTab, val => {
  setIsEnTab(val);
});
onMounted(async () => {
  const main = document.querySelector('main');
  main.classList.add('login-page');
  updateRail?.(true);
});
</script>

<template>
  <div class="main-overview restricted-width">
    <en-overlay-spinner v-model="loading"></en-overlay-spinner>

    <en-widget-card
        :max-width="450"
        :icon-class="'icon-enloc_logo_min'"
        :title="t('common.registerToEngine')"
    >
      <template #default>
        <template v-if="errorList && errorList.length">
          <en-validation-error-message
              v-for="(error, idx) in errorList"
              :key="idx"
              :error="error"
          ></en-validation-error-message>
        </template>

        <v-form ref="form" method="post" class="w-100" @submit.prevent="tryLogin">
          <div class="mt-7 mb-3 w-100">
            <div class="label">{{ t('Authentication.login.userName') }}</div>
            <en-text-field
                id="form-input-username"
                variant="outlined"
                type="email"
                density="compact"
                name="EmailOrUsername"
                v-model="username"
            ></en-text-field>
          </div>

          <div class="w-100">
            <div class="label">{{ t('Authentication.login.password') }}</div>
            <en-text-field
                id="form-input-password"
                name="Password"
                variant="outlined"
                density="compact"
                v-model="password"
                :rules="rules.required"
                :type="showPassword ? 'text' : 'password'"
                :suffix-icon-class="password ? (showPassword ? 'mdi-eye-off' : 'mdi-eye') : null"
                :on-suffix-icon-click="togglePasswordVisibility"
            ></en-text-field>
          </div>

          <input name="__RequestVerificationToken" type="hidden" :value="antiForgery"/>
          <input name="buttonValue" type="hidden" value="login"/>

          <div class="forgot-password d-flex justify-end w-100">
            <a :href="forgetPasswordUrl">{{ t('common.forgotPassword') }}</a>
          </div>

          <en-button
              id="form-input-submit"
              width="100%"
              size="large"
              height="42px"
              native-type="submit"
              :variant="EnButtonVariants.FILLED"
              :loading="loading"
              :disabled="!username || !password"
          >
            {{ t('Authentication.login.buttonText') }}
          </en-button>
        </v-form>

        <div class="d-flex align-center justify-center w-100">
          <v-divider class="flex-grow-1"></v-divider>
          <span class="or-text px-3">{{ t('common.or') }}</span>
          <v-divider class="flex-grow-1"></v-divider>
        </div>

        <template v-if="externalProviders && externalProviders.length > 0">
          <en-button
              v-for="provider in externalProviders"
              :key="provider.AuthenticationScheme"
              width="100%"
              class="button-text"
              elevation="0"
              :href="externalUrl(provider)"
          >
            <div class="d-flex align-center justify-center">
              <en-icon left size="20" class="mr-2">mdi-microsoft</en-icon>
              <span>{{ t('common.registerWithMicrosoft') }}</span>
            </div>
          </en-button>
        </template>
      </template>

      <template #actions>
        <v-card-actions class="find-out-more justify-center w-100" v-if="!isEnTab">
          <span class="new-to-engine mr-1">{{ t('common.newToEngine') }}</span>
          <a :href="registerLink" v-if="shortAccountName">{{ t('Authentication.login.registrationRedirectButtonText') }}</a>
          <a :href="findOutMoreUrl" v-else>{{ t('common.findOutMore') }}</a>
        </v-card-actions>
      </template>
    </en-widget-card>
  </div>

  <div class="global-links" v-if="!isEnTab">
    <div>
      <a :href="supportSubject">{{ t('Authentication.links.support') }}</a>
    </div>
    <div>
      <a :href="HelpLinks.Imprint">{{ t('Authentication.links.imprint') }}</a>
    </div>
    <div>
      <a :href="HelpLinks.PrivacyPolicy">{{ t('Authentication.links.privacyPolicy') }}</a>
    </div>
  </div>
</template>


<style lang="scss" scoped>
@use 'sass:math';
@use "Styles/helper/variables.scss";

.global-links {
  color: #556570;
  font-size: 1rem;
  display: flex;
  gap: 11px;
  padding-bottom: 20px;
}

@media (min-height: 800px) {
  .global-links {
    position: fixed;
    bottom: 20px;
    gap: 21px;
  }
}

.status-bar {
  width: 100%;
  height: 100%;
}

.form-group-bordered h5 {
  margin-bottom: 50px;
}

.btn.btn-filled {
  display: inline-block;
  text-align: center;
}

.form-group {
  margin-top: 0;
}

.footer-link-list {
  & > div:not(:first-child)::before {
    content: "|";
    position: absolute;
    left: 0;
  }

  .link-hide::before {
    content: "" !important;
  }

  & > div:first-child::before {
    content: "";
  }

  & > div {
    position: relative;
    padding: 0 8px;
  }
}

.footer-copyright {
  padding-left: 8px;
}

.v-card {
  width: 450px;
  border: 1.5px solid rgba(0, 0, 0, 0.1) !important;
  box-shadow: none !important;
  border-radius: 12px;
}

:deep(.v-input__details) {
  display: none !important;
}

.label {
  display: block;
  font-size: 0.875rem;
  color: variables.$en-blue !important;
}

.justify-end {
  margin-bottom: 24px;
}


.v-card-actions {
  padding-top: 24px;
  padding-bottom: 24px;
  justify-content: center;
  min-height: auto;
}

.w-100 {
  width: 100%;
}

.button-text {
  border: 1px solid variables.$login-page-btn-text;
  text-transform: none;
}

.en-icon-container {
  border: 1px solid variables.$login-page-icon-container-border;
  border-radius: 12px;
  width: 60px;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
}

.forgot-password a {
  margin-top: 5px;
  text-decoration: none;
  color: variables.$en-red;
}

.find-out-more {
  background: variables.$login-page-find-out-more-bg;
  margin-top: 32px;
  margin-bottom: -19px;
  border-radius: 8px;
  height: 10px !important;

  a {
    color: variables.$en-red;
    text-decoration: none;
  }
}

.or-text {
  color: variables.$login-page-find-or-text;
}

.new-to-engine {
  color: variables.$en-blue;
}

.en-title {
  font-size: 1.5rem;
  font-weight: 500 !important;
  margin-top: 10px;
  color: variables.$en-blue !important;
}

.en-icon {
  color: variables.$en-red !important;
}


:global(main.main-v2.login-page) {
  margin-left: 0;
  height: (100vh - #{variables.$navigation-bar-height});
}
</style>
